/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import { ResponsiveHeroImage, LazyLoadImages, LoadMoreButton, wrapCollectionLayout } from "@quintype/components";
import { CustomLink } from "../../custom-link";
import "./three-col-grid.module.css";
import {
  altTagOfHeroImages,
  formatter,
  isExternalStory,
  isEager,
  getRowMetadata,
  updateStoriesWithAlternatives,
  getStoryUrl
} from "../../../../isomorphic/utils";
import TimeAgo from "react-timeago";
import PropTypes from "prop-types";
import { ImageFallback } from "../../basic/image-fallback";
import get from "lodash/get";
import { SvgHandler } from "../../svg-handler/svg-handler";
import { CollectionName } from "../../atoms/collection-name/index.js";
import dotIcon from "../../../../assets/icons/dot.svg";

function ThreeColGridBase({
  stories,
  config,
  collection,
  onLoadMore,
  noMoreStories = true,
  associatedMetadata,
  index
}) {
  const pbAheadConfig = getRowMetadata(config, associatedMetadata);
  const {
    enableDarkTheme = true,
    show_collection_name: showCollectionName = true,
    show_author_name: showAuthorName = true,
    show_time_of_publish: showPublishDate = true,
    show_section_tag: showSectionTag = true,
    limitStories = 0
  } = pbAheadConfig;
  const alwaysEager = isEager(index, true);
  const desktopEager = isEager(index);

  function getHeroImage(story, eager) {
    const image = get(story, ["hero-image-s3-key"]);

    if (image) {
      return (
        <figure styleName={`hero-image ${story["story-template"] === "video" ? "video-icon" : ""}`}>
          <LazyLoadImages>
            <ResponsiveHeroImage
              story={story}
              aspectRatio={[16, 9]}
              alt={altTagOfHeroImages(story)}
              defaultWidth={480}
              widths={[250, 400]}
              sizes="(max-width: 500px) 98vw, (max-width: 768px) 48vw, 23vw"
              imgParams={{ auto: ["format", "compress"], fit: "max" }}
              eager={eager}
            />
          </LazyLoadImages>
        </figure>
      );
    }
    return (
      <div>
        <ImageFallback styleName="image-cover" story={story} />
      </div>
    );
  }
  function getDotIcon() {
    return showPublishDate && showAuthorName ? (
      <span styleName="dot-icon">
        <SvgHandler xlinkHref={dotIcon} width="4px" height="4px" fill={`${enableDarkTheme ? "#fff" : ""}`} />
      </span>
    ) : null;
  }

  function getContent(story) {
    const section = get(story, ["sections", 0, "name"]);
    const publishDate = get(story, ["last-published-at"], story["first-published-at"]);
    return (
      <div styleName={`content-wrapper ${enableDarkTheme ? "theme-dark" : ""}`}>
        {showSectionTag && (
          <span data-testid="three-col-grid-section" styleName="section-tag">
            {section}
          </span>
        )}
        <p data-testid="three-col-grid-headline" styleName="headline">
          {story.headline}
        </p>
        <div data-testid="three-col-grid-create" styleName="create-at">
          {showAuthorName && <div>{story["author-name"]}</div>}
          {getDotIcon()}
          <div>
            {showPublishDate && publishDate && (
              <TimeAgo
                date={publishDate}
                formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date, config)}
                title={null}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (limitStories) stories = stories.slice(0, limitStories);
  stories = updateStoriesWithAlternatives(stories, false);
  return (
    <div data-testid="three-col-grid-wrapper" styleName="collection-wrapper">
      {showCollectionName && <CollectionName name={collection.name} config={config} section={collection} />}
      <div data-testid="three-col-grid-stories" styleName="three-col-component-wrapper">
        {stories.map((story, index) => {
          const eager = index < 2 ? alwaysEager : desktopEager;
          return (
            <div key={story.id} styleName="story-item">
              <CustomLink href={getStoryUrl(story)} externalLink={isExternalStory(story)} styleName="three-col-card">
                <div styleName="image-wrapper">{getHeroImage(story, eager)}</div>
                {getContent(story)}
              </CustomLink>
            </div>
          );
        })}
      </div>
      {!noMoreStories && <LoadMoreButton className="load-more-button" onLoadMore={onLoadMore} text="Load More" />}
    </div>
  );
}

ThreeColGridBase.propTypes = {
  stories: PropTypes.arrayOf(PropTypes.object),
  config: PropTypes.object,
  collection: PropTypes.object,
  associatedMetadata: PropTypes.object,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool,
  index: PropTypes.number
};

export const ThreeColGrid = wrapCollectionLayout(ThreeColGridBase);

ThreeColGrid.storyLimit = 18;
